import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Fade,
  CircularProgress,
  Chip,
  makeStyles,
  useTheme
} from "@material-ui/core";
import {
  Settings,
  CallEnd,
  Call,
  RecordVoiceOver
} from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import VoiceStreamingControls from "../VoiceStreamingControls";
import VoiceSettingsPanel from "../VoiceSettingsPanel";
import VoiceMessageHistory from "../VoiceMessageHistory";
import ConnectionStatusIndicator from "../ConnectionStatusIndicator";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "relative",
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden"
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 64
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  content: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  },
  conversationArea: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  controlsArea: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper
  },
  statusChip: {
    marginLeft: theme.spacing(1)
  },
  centeredContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    textAlign: "center",
    padding: theme.spacing(4)
  },
  welcomeIcon: {
    fontSize: 64,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2)
  },
  settingsPanel: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 300,
    height: "100%",
    backgroundColor: theme.palette.background.paper,
    borderLeft: `1px solid ${theme.palette.divider}`,
    transform: "translateX(100%)",
    transition: theme.transitions.create("transform"),
    zIndex: 1000
  },
  settingsPanelOpen: {
    transform: "translateX(0)"
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 999
  }
}));

const VoiceAssistantInterface = ({
  ticketId,
  companyId,
  assistantId,
  onVoiceMessage,
  onClose,
  onConnectionStatusChange,
  onActiveStateChange
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { user } = useContext(AuthContext);
  
  // State management
  const [isActive, setIsActive] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentTranscription, setCurrentTranscription] = useState("");
  const [voiceSettings, setVoiceSettings] = useState({
    selectedVoice: "alloy",
    speed: 1.0,
    volume: 0.8,
    autoPlay: true
  });

  // Refs
  const socketRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // Initialize voice assistant
  const initializeVoiceAssistant = useCallback(async () => {
    try {
      setConnectionStatus("connecting");
      
      // Initialize socket connection
      socketRef.current = socketConnection({ user });
      
      if (socketRef.current) {
        // Setup socket listeners
        socketRef.current.on("voice-assistant:connected", () => {
          setIsConnected(true);
          setConnectionStatus("connected");
          onConnectionStatusChange && onConnectionStatusChange("connected");
          toastUtils.success(i18n.t("voiceAssistant.streaming.connected"), "Assistente de Voz Conectado");
        });
        
        socketRef.current.on("voice-assistant:disconnected", () => {
          setIsConnected(false);
          setConnectionStatus("disconnected");
          setIsActive(false);
          onConnectionStatusChange && onConnectionStatusChange("disconnected");
          onActiveStateChange && onActiveStateChange(false);
        });
        
        socketRef.current.on("voice-assistant:transcription", (data) => {
          setCurrentTranscription(data.text);
          if (data.final) {
            addMessage({
              type: "user",
              content: data.text,
              timestamp: new Date(),
              audioUrl: data.audioUrl
            });
            setCurrentTranscription("");
          }
        });
        
        socketRef.current.on("voice-assistant:response", (data) => {
          addMessage({
            type: "assistant",
            content: data.text,
            timestamp: new Date(),
            audioUrl: data.audioUrl,
            voice: data.voice
          });
          setIsProcessing(false);
        });
        
        socketRef.current.on("voice-assistant:audio-start", () => {
          setIsSpeaking(true);
        });
        
        socketRef.current.on("voice-assistant:audio-end", () => {
          setIsSpeaking(false);
        });
        
        socketRef.current.on("voice-assistant:error", (error) => {
          console.error("Voice assistant error:", error);
          setConnectionStatus("error");
          onConnectionStatusChange && onConnectionStatusChange("error");
          setIsProcessing(false);
          setIsRecording(false);
          setIsSpeaking(false);
          toastError({ message: error.message || i18n.t("voiceAssistant.streaming.connectionError") });
        });
        
        // Connect to voice assistant
        socketRef.current.emit("voice-assistant:connect", {
          ticketId,
          companyId,
          userId: user.id,
          assistantId,
          settings: voiceSettings
        });
      }
    } catch (error) {
      console.error("Error initializing voice assistant:", error);
      setConnectionStatus("error");
      onConnectionStatusChange && onConnectionStatusChange("error");
      toastError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId, companyId, user.id, assistantId, voiceSettings]);

  // Add message to conversation
  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, { ...message, id: Date.now() }]);
  }, []);

  // Start voice session
  const startVoiceSession = useCallback(async () => {
    try {
      if (!isConnected) {
        await initializeVoiceAssistant();
        return;
      }

      setIsActive(true);
      onActiveStateChange && onActiveStateChange(true);
      toastUtils.success(i18n.t("voiceAssistant.streaming.sessionActive"), "Sessão de Voz Ativa");
      
      // Emit session start
      if (socketRef.current) {
        socketRef.current.emit("voice-assistant:start-session", {
          ticketId,
          settings: voiceSettings
        });
      }
    } catch (error) {
      console.error("Error starting voice session:", error);
      toastError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, initializeVoiceAssistant, ticketId, voiceSettings]);

  // End voice session
  const endVoiceSession = useCallback(() => {
    try {
      setIsActive(false);
      setIsRecording(false);
      setIsProcessing(false);
      setIsSpeaking(false);
      onActiveStateChange && onActiveStateChange(false);
      
      if (socketRef.current) {
        socketRef.current.emit("voice-assistant:end-session");
      }
      
      // Clean up audio resources
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      toastUtils.info(i18n.t("voiceAssistant.streaming.sessionEnded"), "Sessão de Voz Finalizada");
    } catch (error) {
      console.error("Error ending voice session:", error);
      toastError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle voice recording
  const handleRecordingStateChange = useCallback((recording) => {
    setIsRecording(recording);
    
    if (recording) {
      setIsProcessing(true);
    }
  }, []);

  // Handle audio processing
  const handleAudioProcessed = useCallback((audioData) => {
    if (socketRef.current && isActive) {
      socketRef.current.emit("voice-assistant:audio-data", {
        audioData,
        format: "webm",
        timestamp: Date.now()
      });
    }
  }, [isActive]);

  // Handle settings change
  const handleSettingsChange = useCallback((newSettings) => {
    setVoiceSettings(prev => ({ ...prev, ...newSettings }));
    
    if (socketRef.current && isConnected) {
      socketRef.current.emit("voice-assistant:update-settings", newSettings);
    }
  }, [isConnected]);

  // Close settings panel
  const handleCloseSettings = useCallback(() => {
    setShowSettings(false);
  }, []);

  // Get status color
  const getStatusColor = useCallback(() => {
    switch (connectionStatus) {
      case "connected": return theme.palette.success.main;
      case "connecting": return theme.palette.warning.main;
      case "error": return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  }, [connectionStatus, theme]);

  // Get status text
  const getStatusText = useCallback(() => {
    if (isActive && isRecording) return i18n.t("voiceAssistant.streaming.recording");
    if (isActive && isProcessing) return i18n.t("voiceAssistant.streaming.processing");
    if (isActive && isSpeaking) return i18n.t("voiceAssistant.streaming.speaking");
    if (isActive) return i18n.t("voiceAssistant.streaming.sessionActive");
    
    switch (connectionStatus) {
      case "connected": return i18n.t("voiceAssistant.streaming.connected");
      case "connecting": return i18n.t("voiceAssistant.streaming.connecting");
      case "error": return i18n.t("voiceAssistant.streaming.connectionError");
      default: return i18n.t("voiceAssistant.streaming.disconnected");
    }
  }, [isActive, isRecording, isProcessing, isSpeaking, connectionStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      endVoiceSession();
      if (socketRef.current) {
        socketRef.current.off("voice-assistant:connected");
        socketRef.current.off("voice-assistant:disconnected");
        socketRef.current.off("voice-assistant:transcription");
        socketRef.current.off("voice-assistant:response");
        socketRef.current.off("voice-assistant:audio-start");
        socketRef.current.off("voice-assistant:audio-end");
        socketRef.current.off("voice-assistant:error");
      }
    };
  }, [endVoiceSession]);

  return (
    <Paper className={classes.root} elevation={2}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <RecordVoiceOver />
          <Box>
            <Typography variant="h6">
              {i18n.t("voiceAssistant.voiceInterface.title")}
            </Typography>
            <Typography variant="caption">
              {i18n.t("voiceAssistant.voiceInterface.subtitle")}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={getStatusText()}
            className={classes.statusChip}
            style={{ backgroundColor: getStatusColor(), color: "white" }}
            icon={
              connectionStatus === "connecting" ? (
                <CircularProgress size={12} color="inherit" />
              ) : null
            }
          />
        </Box>
        
        <Box className={classes.headerActions}>
          <ConnectionStatusIndicator
            status={connectionStatus}
            isActive={isActive}
          />
          
          <Tooltip title={i18n.t("voiceAssistant.streaming.voiceSettings")}>
            <IconButton
              color="inherit"
              onClick={() => setShowSettings(true)}
            >
              <Settings />
            </IconButton>
          </Tooltip>
          
          {onClose && (
            <Tooltip title={i18n.t("buttons.close")}>
              <IconButton color="inherit" onClick={onClose}>
                <CallEnd />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Box className={classes.content}>
        {!isActive && messages.length === 0 ? (
          // Welcome Screen
          <Box className={classes.centeredContent}>
            <RecordVoiceOver className={classes.welcomeIcon} />
            <Typography variant="h6" gutterBottom>
              {i18n.t("voiceAssistant.voiceInterface.title")}
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {i18n.t("voiceAssistant.voiceInterface.subtitle")}
            </Typography>
            <Box mt={3}>
              <IconButton
                color="primary"
                size="large"
                onClick={startVoiceSession}
                disabled={connectionStatus === "connecting"}
                style={{
                  width: 80,
                  height: 80,
                  backgroundColor: theme.palette.primary.main,
                  color: "white"
                }}
              >
                {connectionStatus === "connecting" ? (
                  <CircularProgress color="inherit" size={32} />
                ) : (
                  <Call fontSize="large" />
                )}
              </IconButton>
            </Box>
            <Typography variant="caption" style={{ marginTop: 8 }}>
              {i18n.t("voiceAssistant.voiceInterface.startConversation")}
            </Typography>
          </Box>
        ) : (
          // Conversation Area
          <Box className={classes.conversationArea}>
            <VoiceMessageHistory
              messages={messages}
              currentTranscription={currentTranscription}
              isProcessing={isProcessing}
            />
          </Box>
        )}
      </Box>

      {/* Controls */}
      {isActive && (
        <Box className={classes.controlsArea}>
          <VoiceStreamingControls
            isRecording={isRecording}
            isProcessing={isProcessing}
            isSpeaking={isSpeaking}
            isConnected={isConnected}
            onRecordingStateChange={handleRecordingStateChange}
            onAudioProcessed={handleAudioProcessed}
            onEndSession={endVoiceSession}
          />
        </Box>
      )}

      {/* Settings Panel */}
      <Fade in={showSettings}>
        <Box>
          {showSettings && (
            <Box className={classes.overlay} onClick={handleCloseSettings} />
          )}
          <Box
            className={`${classes.settingsPanel} ${
              showSettings ? classes.settingsPanelOpen : ""
            }`}
          >
            <VoiceSettingsPanel
              settings={voiceSettings}
              onSettingsChange={handleSettingsChange}
              onClose={handleCloseSettings}
            />
          </Box>
        </Box>
      </Fade>
    </Paper>
  );
};

export default VoiceAssistantInterface;